const WHOLE_SCREEN_REQUIRED_MESSAGE = 'Please share your entire screen, not a browser tab or window.';
const DISPLAY_AUDIO_UNAVAILABLE_KEY = 'snaptest_display_audio_unavailable';

let displayAudioUnavailableMemory = false;

export class ScreenShareSurfaceError extends Error {
  constructor(message = WHOLE_SCREEN_REQUIRED_MESSAGE) {
    super(message);
    this.name = 'ScreenShareSurfaceError';
  }
}

export const buildDisplayMediaConstraints = ({ audio = true } = {}) => {
  const constraints = {
    video: {
      displaySurface: 'monitor',
    },
    monitorTypeSurfaces: 'include',
    selfBrowserSurface: 'exclude',
  };

  if (audio) {
    constraints.audio = {
      suppressLocalAudioPlayback: false,
    };
    constraints.systemAudio = 'include';
  } else {
    constraints.audio = false;
  }

  return constraints;
};

const getSessionStorage = () => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    return window.sessionStorage;
  } catch (e) {
    return null;
  }
};

export const isDisplayAudioUnavailableForSession = () => {
  const storage = getSessionStorage();
  if (!storage) return displayAudioUnavailableMemory;
  return storage.getItem(DISPLAY_AUDIO_UNAVAILABLE_KEY) === 'true';
};

export const markDisplayAudioUnavailableForSession = () => {
  displayAudioUnavailableMemory = true;
  const storage = getSessionStorage();
  if (storage) {
    storage.setItem(DISPLAY_AUDIO_UNAVAILABLE_KEY, 'true');
  }
};

export const clearDisplayAudioUnavailableForSession = () => {
  displayAudioUnavailableMemory = false;
  const storage = getSessionStorage();
  if (storage) {
    storage.removeItem(DISPLAY_AUDIO_UNAVAILABLE_KEY);
  }
};

export const stopStream = (stream) => {
  if (!stream || typeof stream.getTracks !== 'function') return;
  stream.getTracks().forEach((track) => {
    if (track && typeof track.stop === 'function') {
      track.stop();
    }
  });
};

export const getScreenShareCaptureInfo = (stream) => {
  const videoTrack = stream?.getVideoTracks?.()[0] || null;
  const settings = videoTrack?.getSettings?.() || {};

  return {
    display_surface: settings.displaySurface || null,
    system_audio_captured: (stream?.getAudioTracks?.() || []).length > 0,
  };
};

export const assertWholeScreenCapture = (stream) => {
  const { display_surface: displaySurface } = getScreenShareCaptureInfo(stream);

  // Some browsers do not report displaySurface. Chrome/Edge do, so enforce it
  // when available and avoid false negatives elsewhere.
  if (displaySurface && displaySurface !== 'monitor') {
    stopStream(stream);
    throw new ScreenShareSurfaceError();
  }
};

export const isAudioSourceNotReadableError = (err) => (
  err?.name === 'NotReadableError'
  && /audio|source/i.test(err?.message || '')
);

export const requestScreenShareStream = async (
  mediaDevices = navigator.mediaDevices,
  { preferSystemAudio = true } = {},
) => {
  if (!preferSystemAudio || isDisplayAudioUnavailableForSession()) {
    const stream = await mediaDevices.getDisplayMedia(
      buildDisplayMediaConstraints({ audio: false })
    );
    assertWholeScreenCapture(stream);
    return stream;
  }

  try {
    const stream = await mediaDevices.getDisplayMedia(
      buildDisplayMediaConstraints({ audio: true })
    );
    assertWholeScreenCapture(stream);
    return stream;
  } catch (err) {
    if (!isAudioSourceNotReadableError(err)) {
      throw err;
    }

    markDisplayAudioUnavailableForSession();
    console.warn('[screenShareCapture] System audio unavailable; retrying screen capture without display audio.');
    const stream = await mediaDevices.getDisplayMedia(
      buildDisplayMediaConstraints({ audio: false })
    );
    assertWholeScreenCapture(stream);
    return stream;
  }
};
